import React from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { Layout } from './components/Layout';
import { AccountSettingsPage } from './pages/AccountSettingsPage';
import { AgentHubPage } from './pages/AgentHubPage';
import { CommunityPage } from './pages/CommunityPage';
import { ContactPage } from './pages/ContactPage';
import { AppreciativeLanguagePage } from './pages/AppreciativeLanguagePage';
import { EvolvePage } from './pages/EvolvePage';
import { PrivacyPage } from './pages/PrivacyPage';
import { DirectMessagesPage } from './pages/DirectMessagesPage';
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { OAuthCallbackPage } from './pages/OAuthCallbackPage';
import { PeopleDiscoveryPage } from './pages/PeopleDiscoveryPage';
import { RegisterPage } from './pages/RegisterPage';
import { SessionPage } from './pages/SessionPage';
import { WorkspacePage } from './pages/WorkspacePage';
import { useAuth } from './providers/AuthProvider';

// ----- Lazy-loaded pages (code splitting) -----
const VerifyEmailPage = React.lazy(() =>
  import('./pages/VerifyEmailPage').then((m) => ({ default: m.VerifyEmailPage }))
);

const ProfileSetupPage = React.lazy(() =>
  import('./pages/ProfileSetupPage').then((m) => ({ default: m.ProfileSetupPage }))
);
const ForgotPasswordPage = React.lazy(() =>
  import('./pages/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage }))
);
const ResetPasswordPage = React.lazy(() =>
  import('./pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage }))
);

// ----- Placeholder pages (replaced as features are implemented) -----

function PlaceholderPage({ title }: { title: string }): React.JSX.Element {
  return (
    <main className="flex flex-col items-center justify-center gap-4 px-4 py-20 font-sans">
      <h1 className="text-2xl font-semibold text-text-primary">{title}</h1>
      <p className="text-text-secondary">AppreciativeAI — coming soon.</p>
    </main>
  );
}

// ----- Route guards -----

/**
 * Allows access if authenticated OR in guest mode.
 */
function ProtectedOrGuestRoute({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { isAuthenticated, isGuest, isLoading } = useAuth();

  if (isLoading) return <></>;
  if (!isAuthenticated && !isGuest) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/**
 * Requires full authentication (no guest).
 */
function ProtectedRoute({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <></>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/**
 * Requires full authentication AND a complete profile (display_name ≥ 2 chars).
 * Redirects to /profile/setup if the user's display name is missing or too short.
 * Does NOT apply to /profile/setup itself (to avoid infinite redirect loops).
 */
function ProfileRequiredRoute({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) return <></>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const displayName = user?.display_name;
  const isProfileIncomplete =
    displayName == null || displayName.trim().length < 2;

  if (isProfileIncomplete && location.pathname !== '/profile/setup') {
    return <Navigate to="/profile/setup" replace />;
  }

  return <>{children}</>;
}

/**
 * Requires Early Tester tier.
 * - Not authenticated → redirect to /login
 * - Authenticated but not early_tester → redirect to /agents
 */
function EarlyTesterRoute({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) return <></>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.user_tier !== 'early_tester') return <Navigate to="/agents" replace />;
  return <>{children}</>;
}

/**
 * Requires admin role.
 */
function AdminRoute({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) return <></>;
  if (!isAuthenticated || !user?.is_admin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

// ----- Main Router -----

export function AppRouter(): React.JSX.Element {
  return (
    <Routes>
      {/* All routes share the Layout (NavBar + Footer) */}
      <Route element={<Layout />}>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/auth/oauth-callback" element={<OAuthCallbackPage />} />
        <Route path="/auth/verify-email" element={<React.Suspense fallback={<></>}><VerifyEmailPage /></React.Suspense>} />
        <Route path="/auth/forgot-password" element={<React.Suspense fallback={<></>}><ForgotPasswordPage /></React.Suspense>} />
        <Route path="/auth/reset-password" element={<React.Suspense fallback={<></>}><ResetPasswordPage /></React.Suspense>} />
        <Route path="/evolve" element={<EvolvePage />} />
        <Route path="/appreciative-language" element={<AppreciativeLanguagePage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/contact" element={<ContactPage />} />

        {/* Agent Hub — accessible to everyone (guests enter guest mode on chat) */}
        <Route path="/agents" element={<AgentHubPage />} />

        {/* Protected or guest routes */}
        <Route
          path="/agents/:agentId/chat"
          element={<SessionPage />}
        />
        <Route
          path="/session/:id"
          element={
            <ProtectedOrGuestRoute>
              <SessionPage />
            </ProtectedOrGuestRoute>
          }
        />

        {/* Protected routes (auth required) */}
        <Route
          path="/profile/setup"
          element={
            <ProtectedRoute>
              <React.Suspense fallback={<></>}>
                <ProfileSetupPage />
              </React.Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/workspace"
          element={
            <EarlyTesterRoute>
              <WorkspacePage />
            </EarlyTesterRoute>
          }
        />
        <Route
          path="/workspace/journeys/:id"
          element={
            <EarlyTesterRoute>
              <PlaceholderPage title="Journey Detail" />
            </EarlyTesterRoute>
          }
        />
        <Route
          path="/community"
          element={
            <ProtectedRoute>
              <CommunityPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/people"
          element={
            <ProfileRequiredRoute>
              <PeopleDiscoveryPage />
            </ProfileRequiredRoute>
          }
        />
        <Route
          path="/messages"
          element={
            <ProfileRequiredRoute>
              <DirectMessagesPage />
            </ProfileRequiredRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProfileRequiredRoute>
              <AccountSettingsPage />
            </ProfileRequiredRoute>
          }
        />
        {/* TODO: /settings/notifications — re-enable when the Notifications tab is wired */}
        <Route
          path="/notifications"
          element={
            <ProfileRequiredRoute>
              <NotificationsPage />
            </ProfileRequiredRoute>
          }
        />

        {/* Admin routes */}
        <Route
          path="/moderation"
          element={
            <AdminRoute>
              <PlaceholderPage title="Moderation Dashboard" />
            </AdminRoute>
          }
        />

        {/* Catch-all */}
        <Route path="*" element={<PlaceholderPage title="Not Found" />} />
      </Route>
    </Routes>
  );
}
